import { createTheme } from '@material-ui/core/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1565c0',
      light: '#42a5f5',
      dark: '#0d47a1',
      contrastText: '#fff',
    },
    secondary: {
      main: '#00897b',
      light: '#4db6ac',
      dark: '#00695c',
      contrastText: '#fff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
    success: {
      main: '#2e7d32',
    },
    error: {
      main: '#c62828',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    h1: {
      fontWeight: 600,
      fontSize: '2rem',
      letterSpacing: '-0.02em',
      color: '#1a237e',
    },
    h2: {
      fontWeight: 600,
      fontSize: '1.5rem',
      letterSpacing: '-0.01em',
      color: '#1a237e',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: '#1a237e',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.125rem',
      color: '#1a237e',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
      color: '#1a237e',
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.9375rem',
      color: '#1a237e',
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.45,
    },
    subtitle1: {
      fontSize: '0.9375rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.8125rem',
      fontWeight: 500,
      color: 'rgba(0, 0, 0, 0.6)',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        body: {
          fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: 8,
        padding: '8px 20px',
        boxShadow: 'none',
        fontSize: '0.875rem',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        },
      },
      contained: {
        '&:hover': {
          boxShadow: '0 4px 12px rgba(21, 101, 192, 0.35)',
        },
      },
    },
    MuiCard: {
      root: {
        borderRadius: 12,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 12,
      },
      elevation1: {
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      },
      elevation2: {
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      },
    },
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: '#1565c0',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
      },
    },
    MuiTabs: {
      root: {
        fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
      },
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.875rem',
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 12,
        padding: '8px',
        fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
      },
    },
    MuiDialogTitle: {
      root: {
        fontSize: '1.25rem',
        fontWeight: 600,
        color: '#1a237e',
      },
    },
    MuiDialogContent: {
      root: {
        fontSize: '0.875rem',
      },
    },
    MuiMenuItem: {
      root: {
        borderRadius: 8,
        margin: '2px 8px',
        fontSize: '0.875rem',
        '&:hover': {
          backgroundColor: 'rgba(21, 101, 192, 0.08)',
        },
      },
    },
    MuiTableCell: {
      root: {
        fontSize: '0.8125rem',
        padding: '8px 12px',
        borderBottom: '1px solid #eceff1',
      },
      head: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        backgroundColor: '#f5f5f5',
        color: 'rgba(0, 0, 0, 0.87)',
      },
    },
    MuiInputLabel: {
      root: {
        fontSize: '0.875rem',
      },
    },
    MuiInputBase: {
      root: {
        fontSize: '0.875rem',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(0,0,0,0.12)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(21, 101, 192, 0.5)',
        },
      },
      input: {
        paddingLeft: '12px',
      },
    },
    MuiInput: {
      input: {
        paddingLeft: '12px',
      },
    },
    MuiOutlinedInput: {
      input: {
        paddingLeft: '16px',
        paddingRight: '16px',
      },
      inputMultiline: {
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    MuiFilledInput: {
      input: {
        paddingLeft: '16px',
      },
    },
    MuiSelect: {
      select: {
        paddingLeft: '16px',
        paddingRight: '40px',
        boxSizing: 'border-box',
      },
    },
    MuiFormLabel: {
      root: {
        fontSize: '0.875rem',
      },
    },
    MuiTypography: {
      body1: {
        fontSize: '0.875rem',
      },
      body2: {
        fontSize: '0.8125rem',
      },
    },
  },
});

export default theme;
